import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Layout, Space } from 'antd';
import { Link } from 'react-router-dom';
import { getSession, useAppSelector } from '@wappsto/redux';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import usePoints from 'hooks/usePoints';
import User from 'features/userMenu';
import Notifications from 'features/notifications';
import { UsageAlert, UsageWarning } from 'features/billingAlerts';
import Authentication from 'features/authentication';
import { POINT_WARNING_PERCENTAGE } from 'util/billing';

const { Header, Content, Footer } = Layout;

interface PageLayoutProps {
  children: React.ReactElement;
  pageTitle: string;
  pageContent?: {
    Header?: React.ElementType;
    Footer?: React.ElementType;
  };
  toggleMenu?: (...args: any[]) => any;
  isCollapsed?: boolean;
  onCollapse?: (...args: any[]) => any;
}

function PageLayout(
  {
    children,
    pageTitle,
    pageContent = { Header: null, Footer: null },
    toggleMenu = null,
    isCollapsed = false
  }: PageLayoutProps,
) {
  const session = useAppSelector(getSession);
  const Icon = isCollapsed ? MenuUnfoldOutlined : MenuFoldOutlined;

  const { points } = usePoints();
  const [warningType, setWarningType] = useState<string | undefined>();

  useEffect(() => {
    if (points) {
      if (points.remaining_percentage === 0) {
        setWarningType('alert');
      } else if (
        points.remaining_percentage >= 0 &&
        points.remaining_percentage <= POINT_WARNING_PERCENTAGE
      ) {
        setWarningType('warning');
      }
    }
  }, [points, warningType]);

  const onToggleMenu = useCallback(() => {
    if (toggleMenu) {
      toggleMenu();
    }
  }, [toggleMenu]);

  return (
    <Layout>
      <Header className="page-header">
        {session ? (
          <>
            <div>
              <Icon className="trigger" onClick={onToggleMenu} />
              {(pageTitle || pageContent.Header) && (
                <span className="page-header-title">
                  <Space>
                    {pageTitle}
                    {pageContent.Header && <pageContent.Header />}
                  </Space>
                </span>
              )}
            </div>
            <Space size="large">
              <UsageWarning visible={warningType === 'warning'} points={points} />
              <Notifications />
              <User />
            </Space>
          </>
        ) : (
          <>
            <Link to="/">
              <div className="wappsto-logo-light" />
            </Link>
            <Authentication buttonStyle={{ margin: 10 }} />
          </>
        )}
      </Header>
      <Content>
        {(warningType === 'alert' || (session as any)?.limitReached) && <UsageAlert points={points} />}
        {children}
      </Content>
      {pageContent.Footer && (
        <Footer>
          <pageContent.Footer />
        </Footer>
      )}
    </Layout>
  );
}

export default PageLayout;
